import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
} from "react-router-dom";
import "./App.scss";
import { Editor } from "../pages/editor/Editor";
import { Profile } from "../pages/users/Profile";
import { About } from "../pages/about/About";
import { NavBar } from "../pages/chrome/NavBar";
import { Treatments } from "../pages/treatments/Treatments";
import { PageNotFound } from "../pages/pageNotFound/pageNotFound";

import DomainsPage from "../pages/libraries/components/DomainManagement/DomainsPage";
import { DataLibraryFunctionsPage } from "../pages/libraries/components/CatalogueDeDonnees/DataLibraryFunctionsPage";

import { ErrorBoundary } from "react-error-boundary";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fab } from '@fortawesome/free-brands-svg-icons';
import { fas } from "@fortawesome/free-solid-svg-icons";
import { Workflow } from "../pages/workflows/Workflow";
import { OneWorkflow } from "../pages/workflows/OneWorkflow";
import "../config/i18n";
import { useAppSelector } from "../hooks/hooks";
import { DashboardOverview } from "../pages/dashboard/DashboardOverview";
import { ManageUsers } from "../pages/users/ManageUsers";
import { Sidebar } from "primereact/sidebar";
import { setDialog, setSidebar } from "./appSlice";
import { useDispatch } from "react-redux";
import type { AppDispatch } from './store';
import { Dialog } from "primereact/dialog";
import { MainSourceExterne } from "../pages/libraries/components/sourceExterne/MainSourceExterne";
import useConfigStore from "../hooks/putConfigInStore";
import CommonToast   from "../components/Toast";
import { useNetwork } from "../hooks/useNetwork";
import { DataLibrarySourcesPage } from "../pages/libraries/components/CatalogueDeDonnees/DataLibrarySourcesPage";
import { HeaderContainer } from "./Header.container";
import { useProfileQuery } from "../pages/users/profileSlice";
import ProtectedRoute from "./protectedRoutes";
import { Authent } from "../pages/authent/authent";
import { Basicauthent } from "../pages/authent/basicauthent";
import { NotSufficientRole } from "../components/notSufficientRole";
import { LabelsSelectorsProvider } from '@kosmos/shared';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';


library.add(...(Object.values(fab) as IconDefinition[]), ...(Object.values(fas) as IconDefinition[]));

function App() {
  const dispatch = useDispatch<AppDispatch>();

  const { theme, sidebar, dialog } = useAppSelector((s) => s.app);

  const {} = useConfigStore();
  const {} = useNetwork();

  const { data: profile } = useProfileQuery();

  return (
    <>
      <ErrorBoundary
        fallback={
          <b className="mr-2 mt-2">
            Erreur applicative. Veuillez contacter votre administrateur{" "}
          </b>
        }
      >
        <div className={theme}>
          <CommonToast />
          <React.Suspense fallback={<div>Loading</div>}>
            <Router>
              <div className={"flex m-0 p-0 app-container"}>
                <HeaderContainer profile={profile} />
                <div className="content">
                  <NavBar />
                  <Sidebar
                    visible={!!sidebar}
                    dismissable={false}
                    position={"right"}
                    onHide={() => dispatch(setSidebar(undefined))}
                  >
                    <div className="h-full">
                      <div className="text-base font-bold">
                        {sidebar && sidebar.title}
                      </div>
                      {sidebar && sidebar.content}
                    </div>
                  </Sidebar>
                  <Dialog
                    header={dialog && dialog.title}
                    visible={!!dialog}
                    draggable={false}
                    style={{ width: dialog && dialog.width ? dialog.width : 'auto' }}
                    modal={true}
                    onHide={() => dispatch(setDialog(undefined))}
                  >
                    {dialog && dialog.content}
                  </Dialog>

                  <div className="main-container">
                    <Routes>
                      <Route
                        path="/dashboard"
                        element={<DashboardOverview />}
                      />
                      <Route
                        path="/workflows"
                        element={
                          <ProtectedRoute roles={["flows.all", "flows_list"]}>
                            <Workflow />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/workflow"
                        element={
                          <ProtectedRoute
                            roles={["flows.all", "flows_teclogs"]}
                          >
                            <OneWorkflow />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/treatments"
                        element={
                          <ProtectedRoute
                            roles={["pipelines.all", "pipelines.list"]}
                          >
                            <Treatments />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/treatments/editor"
                        element={
                          <ProtectedRoute
                            roles={["pipelines.all", "pipelines.list"]}
                          >
                            <Editor />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/library/source-externe/:id"
                        element={
                          <ProtectedRoute roles={["gds.manage", "gds.read"]}>
                            <LabelsSelectorsProvider serviceUrl="/api/labels?vstoreobject=storage">
                              <MainSourceExterne />
                            </LabelsSelectorsProvider>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/library/source-externe/"
                        element={
                          <ProtectedRoute roles={["gds.manage", "gds.read"]}>
                            <LabelsSelectorsProvider serviceUrl="/api/labels?vstoreobject=storage">
                              <MainSourceExterne />
                            </LabelsSelectorsProvider>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/library/sources"
                        element={<DataLibrarySourcesPage />}
                      />
                      <Route
                        path="/library/sources/:sourceCreatedId"
                        element={
                          <ProtectedRoute roles={["gds.manage", "gds.read"]}>
                            <DataLibrarySourcesPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/library/domain-management"
                        element={
                          <ProtectedRoute roles={["gds.manage", "gds.read"]}>
                            <DomainsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/library/functions" element={
                        <DataLibraryFunctionsPage/>
                      } />

                      <Route path="/manage/users" element={<ManageUsers />} />
                      <Route path="/profile" element={<Profile />} />

                      <Route path="/about" element={<About />} />
                      <Route
                        path="/roles-insuffisants"
                        element={<NotSufficientRole />}
                      />
                      <Route path="/" element={<DashboardOverview />} />
                      <Route path="/auth" element={<Authent />} />
                      <Route path="/basicauth" element={<Basicauthent />} />
                      <Route path="*" element={<PageNotFound />} />
                    </Routes>
                  </div>
                </div>
              </div>
            </Router>
            {/* </ProvideAuth> */}
          </React.Suspense>
        </div>
      </ErrorBoundary>
    </>
  );
}

export default App;
